import { ReactNode } from 'react';

interface InformationalContentProps {
  title: string;
  description: string;
  content: ReactNode;
  faqs: readonly { question: string; answer: string }[];
  relatedTools: readonly { name: string; url: string; description: string }[];
}

export function InformationalContent({
  title,
  description,
  content,
  faqs,
  relatedTools,
}: InformationalContentProps) {
  return (
    <div className="space-y-12">
      {/* Main Content */}
      <section>
        <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-800">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            About this tool
          </p>
          <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
            {title}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            {description}
          </p>
        </div>

        <div className="informational-content-body space-y-8 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
          {content}
        </div>
      </section>

      {/* FAQs Section */}
      {faqs.length > 0 && (
        <section>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-50">
            Frequently asked questions
          </h2>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-slate-50/60 dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900/60">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="px-4 py-4 first:rounded-t-2xl last:rounded-b-2xl sm:px-6"
              >
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                  {faq.question}
                </h3>
                <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related Tools */}
      {relatedTools.length > 0 && (
        <section>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-50">
            Explore related tools
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {relatedTools.map((tool, index) => (
              <a
                key={index}
                href={tool.url}
                className="group flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow-sm transition hover:-translate-y-0.5 hover:border-[#4F46E5]/60 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-400/70"
              >
                <div>
                  <h3 className="text-sm font-semibold text-[#4F46E5] group-hover:text-[#4338CA] dark:text-indigo-300 dark:group-hover:text-indigo-200">
                    {tool.name}
                  </h3>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {tool.description}
                  </p>
                </div>
                <span className="mt-3 text-[11px] font-medium text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300">
                  Open tool →
                </span>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

